import root from 'window-or-global';
import {lowercaseObjectProperties} from '../src/utils/lowercaseObjectProperties';

/**
 * @typedef PurchaseReceiptOffers
 * @property {string} OfferId
 * @property {string} OfferNamespace
 *
 * @typedef GrantedVoucher
 * @property {string} amountDisplay
 * @property {string} code: string
 * @property {string} endDateDisplay
 * @property {string} epicSalesEventId
 * @property {string} epicSalesEventSlug
 * @property {string} label
 * @property {string} link
 * @property {string} minSalesPriceDisplay
 * @property {string} voucherImages
 *
 * @typedef PurchaseReceipt
 * @property {GrantedVoucher} grantedVoucher
 * @property {Array<PurchaseReceiptOffers>} Offers
 * @property {string} TransactionId: string;
 * @property {string} TransactionState: string;
 */

export const RequestCloseReasonType = {
    LoadFailure: 'LoadFailure',
    UserCanceled: 'UserCanceled',
    WasSuccessful: 'WasSuccessful',
};

export const iOSNativeUserAgent = /.*(EpicGamesIOSClient).*/;
const handlerName = 'nativeIOSHandler';

class IOSNativePurchaseIntegration {
    patch() {
        //patch bridge (preserve [this] scope)
        root.ue.PurchaseFlow.Receipt = this.handleReceipt.bind(this);
        root.ue.PurchaseFlow.RequestClose = this.handleRequestClose.bind(this);

        lowercaseObjectProperties(root.ue);
    }

    /**
     * Returns true/false if the [userAgent] or [window.navigator.userAgent] contain [EpicGamesIOSClient]
     * @param {string} [userAgent=window.navigator.userAgent]
     * @returns {boolean}
     */
    isIOSNativeClient(userAgent) {
        return iOSNativeUserAgent.test(userAgent || root.navigator.userAgent);
    }

    /**
     * Validates that the iOS native app has added the correct api to the [window] object
     * @returns {boolean}
     */
    hasIOSNativeBridgeApi() {
        return !!(
            root &&
            root.webkit &&
            root.webkit.messageHandlers &&
            root.webkit.messageHandlers[handlerName] &&
            root.webkit.messageHandlers[handlerName].postMessage
        );
    }

    /**
     * On purchase success, this method communicates back to the iOS layer with the purchase receipt
     * NOTE: This bridge method is triggered by the purchase flow WebView
     * @param {PurchaseReceipt | null} receipt
     * @returns {Promise<PurchaseReceipt | null>}
     */
    handleReceipt(receipt) {
        return new Promise((resolve, reject) => {
            if (!receipt) {
                reject(new Error('Purchase success, but receipt was undefined'));
            }

            if (!receipt.TransactionId || receipt.TransactionState !== 'Purchased') {
                reject(
                    new Error(
                        `Purchase success, but receipt was invalid (id: ${receipt.TransactionId}, state: ${receipt.TransactionState})`
                    )
                );
            }

            if (!this.hasIOSNativeBridgeApi()) {
                reject(
                    new Error(
                        `Purchase success, but iOS native message handler: ${handlerName}, is missing (${receipt.TransactionId})`
                    )
                );
            }

            // temp store recipe object so we can dispatch event during handleRequestClose
            this.lastReceipt = receipt;

            resolve(receipt);
        });
    }

    /**
     * Called when the purchase flow is closing during success, failure, or cancel
     * NOTE: This bridge method is triggered by the purchase flow WebView
     * @param {string} value
     */
    handleRequestClose(value) {
        if (!this.hasIOSNativeBridgeApi()) {
            return;
        }

        if (value === RequestCloseReasonType.WasSuccessful) {
            const data = JSON.stringify(this.lastReceipt);
            this.lastReceipt = null;

            return root.webkit.messageHandlers[handlerName].postMessage(
                {
                    type: 'asyncRequest',
                    command: 'purchaseDidFinish',
                    args: {
                        status: 'success',
                        data,
                    },
                },
                root.origin
            );
        }
        // all other Reason Types are considered failures
        root.webkit.messageHandlers[handlerName].postMessage(
            {
                type: 'asyncRequest',
                command: 'purchaseDidFinish',
                args: {
                    status: 'failure',
                    data: value,
                },
            },
            root.origin
        );
    }
}

const integration = new IOSNativePurchaseIntegration();
export default integration;
